import { Link } from "react-router-dom";
import Seo, { SITE_URL } from "@/components/feature/Seo";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import { siteInfo } from "@/mocks/gossipData";
import ContactForm from "./components/ContactForm";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: "联系我们 - 瓜田福利社",
    description: "通过表单或邮件联系瓜田福利社，提交选题建议、内容纠错或合作咨询。",
    url: `${SITE_URL}/contact`,
  },
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: siteInfo.name,
    url: SITE_URL,
    email: siteInfo.email,
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "联系我们", item: `${SITE_URL}/contact` },
    ],
  },
];

const reasons = [
  {
    icon: "ri-lightbulb-line",
    title: "选题建议",
    desc: "希望看到哪类合集？告诉我们方向，被采纳的选题会优先安排。",
  },
  {
    icon: "ri-error-warning-line",
    title: "内容纠错",
    desc: "发现事实错误或表述不当，欢迎附上依据，我们会核实后尽快修正。",
  },
  {
    icon: "ri-team-line",
    title: "合作咨询",
    desc: "内容合作、栏目共建等需求，请简要说明背景，我们会尽快回复。",
  },
];

export default function ContactPage() {
  return (
    <>
      <Seo
        title="联系我们｜瓜田福利社选题建议与内容反馈"
        description="通过留言表单或邮件联系瓜田福利社，提交选题建议、内容纠错或合作咨询，工作日 24 小时内回复。"
        keywords="联系瓜田福利社,选题建议,内容纠错,合作咨询,联系方式"
        path="/contact"
        ogType="website"
        jsonLd={jsonLd}
      />
      <SiteLayout>
        <section className="w-full bg-background-100 px-4 py-10 md:px-6 md:py-14">
          <Breadcrumbs items={[{ label: "联系我们" }]} />
          <div className="mt-5 flex flex-col gap-4">
            <h1 className="font-heading text-3xl text-foreground-950 md:text-5xl">联系我们</h1>
            <p className="max-w-3xl text-sm leading-relaxed text-foreground-600 md:text-base">
              有想看的选题、发现了内容问题，或者想聊聊合作，都可以通过下面的表单留言。
              我们会在{siteInfo.replyTime}。
            </p>
          </div>
        </section>

        <section className="w-full px-4 py-12 md:px-6 md:py-16">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-7">
              <ContactForm />
            </div>

            <div className="flex flex-col gap-6 lg:col-span-5">
              <div className="rounded-2xl border border-background-200 bg-background-100 p-6 md:p-7">
                <h2 className="font-heading text-xl text-foreground-950">直接联系我们</h2>
                <ul className="mt-5 flex flex-col gap-4 text-sm">
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                      <i className="ri-mail-line" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-foreground-500">邮箱</span>
                      <a
                        href={`mailto:${siteInfo.email}`}
                        className="text-foreground-900 transition-colors hover:text-primary-600 cursor-pointer"
                      >
                        {siteInfo.email}
                      </a>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                      <i className="ri-time-line" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-foreground-500">回复时间</span>
                      <span className="text-foreground-900">{siteInfo.replyTime}</span>
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-background-50 text-primary-600">
                      <i className="ri-refresh-line" aria-hidden="true" />
                    </span>
                    <span className="flex flex-col gap-0.5">
                      <span className="text-foreground-500">内容更新</span>
                      <span className="text-foreground-900">{siteInfo.updateFreq}</span>
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col gap-4 rounded-2xl border border-background-200 bg-background-50 p-6 md:p-7">
                <h2 className="font-heading text-xl text-foreground-950">常见联系用途</h2>
                {reasons.map((item) => (
                  <div key={item.title} className="flex gap-3">
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                      <i className={`${item.icon} text-lg`} aria-hidden="true" />
                    </span>
                    <div>
                      <h3 className="text-sm font-label text-foreground-900">{item.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-foreground-600">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="rounded-2xl bg-secondary-950 p-6 text-background-100 md:p-7">
                <h2 className="font-heading text-xl text-background-50">先逛逛合集？</h2>
                <p className="mt-2 text-sm leading-relaxed text-background-200">
                  很多问题在合集列表页就能找到答案，用分类和关键词快速定位你想看的内容。
                </p>
                <Link
                  to="/gossip"
                  className="mt-5 flex w-fit items-center gap-2 whitespace-nowrap rounded-full bg-accent-500 px-5 py-2.5 text-sm font-label text-secondary-950 transition-colors hover:bg-accent-400 cursor-pointer"
                >
                  <i className="ri-compass-3-line" aria-hidden="true" />
                  浏览吃瓜合集
                </Link>
              </div>
            </div>
          </div>
        </section>
      </SiteLayout>
    </>
  );
}